/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Descriptions, Select, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { getAxisWithAdminUnits } from '../../services/pos/pos';

const ZonalRestriction = ({ form, name, title }) => {
  const { t } = useTranslation();

  const [axis, setAxis] = useState([]);
  const [uats, setUats] = useState([]);
  const [selectedAx, setSelectedAx] = useState(null);
  const [isPrefilled, setIsPrefilled] = useState(false);
  const ZERO_TRANSITIONS_NO = 0;
  const [productIdx, _rest, restrictionIdx] = name;

  const values = form.getFieldsValue([
    ['product', 'payload', productIdx, 'restrictions', restrictionIdx],
  ]);
  const res = values.product.payload[productIdx].restrictions[restrictionIdx];
  const { value1, value2 } = res;

  useEffect(() => {
    getAxisWithAdminUnits().then((response) => {
      if (value1 !== undefined && value1 === 0 && value2 === 0) {
        const adminUnits = Array.from(
          new Map(
            response
              .flatMap((axisList) => axisList.administrativeUnits ?? [])
              .map((unit) => [unit.id, unit]),
          ).values(),
        );
        setUats(adminUnits);
        setAxis(response);
      } else if (value1 !== undefined && value1 === 0 && value2 > 0) {
        const axisWithAtLeastPassTransitions = response.filter(
          (aau) => aau.administrativeUnits.length >= value2 + 1,
        );
        setAxis(axisWithAtLeastPassTransitions);
      } else {
        setAxis(response);
      }
    });
  }, [form, value1, value2]);

  // Auto-select axis and populate options based on title's UAT data
  useEffect(() => {
    if (axis.length > 0 && title?.uatStart && title?.uatStop) {
      // Find the axis that contains both uatStart and uatStop administrative units
      const matchingAxis = axis.find((ax) => {
        const adminUnits = ax.administrativeUnits || [];
        const hasUatStart = adminUnits.some(
          (unit) => unit.code === title.uatStart,
        );
        const hasUatStop = adminUnits.some(
          (unit) => unit.code === title.uatStop,
        );
        return hasUatStart && hasUatStop;
      });

      if (matchingAxis) {
        // Set the selected axis
        setSelectedAx(matchingAxis.code);

        // Find the IDs of the administrative units
        const uatStartUnit = matchingAxis.administrativeUnits.find(
          (unit) => unit.code === title.uatStart,
        );
        const uatStopUnit = matchingAxis.administrativeUnits.find(
          (unit) => unit.code === title.uatStop,
        );

        // Set form values
        form.setFieldsValue({
          product: {
            payload: {
              [productIdx]: {
                restrictions: {
                  [restrictionIdx]: {
                    axis: matchingAxis.code,
                    option1: uatStartUnit?.id,
                    option2: uatStopUnit?.id,
                  },
                },
              },
            },
          },
        });

        // Mark as prefilled to disable the fields
        setIsPrefilled(true);
      }
    }
  }, [axis, title, form, productIdx, restrictionIdx]);

  const selectedAxis = axis.find((ax) => ax.code === selectedAx);

  return (
    <Descriptions bordered column={1}>
      {value1 !== undefined &&
        // eslint-disable-next-line no-nested-ternary
        (value1 === 0 ? (
          value2 === 0 ? (
            <Descriptions.Item
              label={t(
                'entity.offers.restrictions.restriction.ZONAL.zeroTransitionsUAT',
              )}
            >
              <Form.Item
                name={[restrictionIdx, 'option1']}
                rules={[
                  {
                    required: true,
                    message: t(
                      'entity.offers.restrictions.restriction.ZONAL.axisRequired',
                    ),
                  },
                ]}
                // noStyle
              >
                <Select
                  placeholder={t(
                    'entity.offers.restrictions.restriction.ZONAL.zeroTransitionsUAT',
                  )}
                  style={{ width: '100%' }}
                  allowClear
                  disabled={isPrefilled}
                  onChange={(value) => {
                    const restriction = form.getFieldValue([
                      'product',
                      'payload',
                      productIdx,
                      'restrictions',
                      restrictionIdx,
                    ]);
                    form.setFieldsValue({
                      product: {
                        payload: {
                          [productIdx]: {
                            restrictions: {
                              [restrictionIdx]: {
                                ...restriction,
                                option1: value,
                                option2: value,
                              },
                            },
                          },
                        },
                      },
                    });
                  }}
                >
                  {uats.map((unit) => (
                    <Select.Option key={unit.id} value={unit.id}>
                      {unit.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name={[restrictionIdx, 'option2']} hidden />
            </Descriptions.Item>
          ) : (
            <>
              {/* Axis select */}
              <Descriptions.Item>
                <Form.Item
                  name={[restrictionIdx, 'axis']}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'entity.offers.restrictions.restriction.ZONAL.selectAxis',
                      ),
                    },
                  ]}
                >
                  <Select
                    placeholder={t(
                      'entity.offers.restrictions.restriction.ZONAL.selectAxis',
                    )}
                    disabled={isPrefilled}
                    onChange={(value) => {
                      setSelectedAx(value);

                      // Reset option1 and option2
                      const restriction = form.getFieldValue([
                        'product',
                        'payload',
                        productIdx,
                        'restrictions',
                        restrictionIdx,
                      ]);
                      form.setFieldsValue({
                        product: {
                          payload: {
                            [productIdx]: {
                              restrictions: {
                                [restrictionIdx]: {
                                  ...restriction,
                                  option1: undefined,
                                  option2: undefined,
                                },
                              },
                            },
                          },
                        },
                      });
                    }}
                    style={{ width: '100%' }}
                  >
                    {axis.map((ax) => (
                      <Select.Option value={ax.code} key={ax.id}>
                        {ax.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Descriptions.Item>

              {/* Option1 select */}
              {selectedAxis && (
                <Descriptions.Item
                  label={t(
                    'entity.offers.restrictions.restriction.ZONAL.uatStart',
                  )}
                >
                  <Form.Item shouldUpdate>
                    {() => {
                      const option2Value = form.getFieldValue([
                        'product',
                        'payload',
                        productIdx,
                        'restrictions',
                        restrictionIdx,
                        'option2',
                      ]);

                      // Compute allowed units for option1 based on value2
                      let allowedOption1 = [];
                      const units = selectedAxis.administrativeUnits || [];

                      if (value2 === 0 && option2Value) {
                        allowedOption1 = units.filter(
                          (u) => String(u.id) === String(option2Value),
                        );
                      } else if (value2 === 1 && option2Value) {
                        const index = units.findIndex(
                          (u) => String(u.id) === String(option2Value),
                        );
                        if (index > 0) allowedOption1.push(units[index - 1]);
                        if (index < units.length - 1)
                          allowedOption1.push(units[index + 1]);
                      } else if (value2 === 2) {
                        if (!option2Value) {
                          allowedOption1 = units.length
                            ? [units[0], units[units.length - 1]]
                            : [];
                        } else {
                          const index = units.findIndex(
                            (u) => String(u.id) === String(option2Value),
                          );
                          if (index === 0 && units.length) {
                            allowedOption1 = [units[units.length - 1]];
                          } else if (
                            index === units.length - 1 &&
                            units.length
                          ) {
                            allowedOption1 = [units[0]];
                          } else if (units.length) {
                            // If a middle element was selected, still restrict to first and last
                            allowedOption1 = [
                              units[0],
                              units[units.length - 1],
                            ];
                          } else {
                            allowedOption1 = [];
                          }
                        }
                      } else {
                        allowedOption1 = units;
                      }

                      return (
                        <Form.Item
                          name={[restrictionIdx, 'option1']}
                          rules={[
                            {
                              required: true,
                              message: t(
                                'entity.offers.restrictions.restriction.ZONAL.axisRequired',
                              ),
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (
                                  !value ||
                                  String(value) !== String(option2Value) ||
                                  value2 === ZERO_TRANSITIONS_NO
                                ) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(
                                  new Error(
                                    t(
                                      'entity.offers.restrictions.restriction.ZONAL.sameUatNotAllowed',
                                    ),
                                  ),
                                );
                              },
                            }),
                          ]}
                        >
                          <Select
                            placeholder={t(
                              'entity.offers.restrictions.restriction.ZONAL.uatStart',
                            )}
                            style={{ width: '100%' }}
                            disabled={isPrefilled}
                            allowClear={!isPrefilled}
                          >
                            {allowedOption1.map((unit) => (
                              <Select.Option key={unit.id} value={unit.id}>
                                {unit.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Descriptions.Item>
              )}

              {/* Option2 select */}
              {selectedAxis && (
                <Descriptions.Item
                  label={t(
                    'entity.offers.restrictions.restriction.ZONAL.uatStop',
                  )}
                >
                  <Form.Item shouldUpdate>
                    {() => {
                      const option1Value = form.getFieldValue([
                        'product',
                        'payload',
                        productIdx,
                        'restrictions',
                        restrictionIdx,
                        'option1',
                      ]);

                      // Compute allowed units for option2 based on value2
                      let allowedOption2 = [];
                      const units = selectedAxis.administrativeUnits || [];

                      if (value2 === 0 && option1Value) {
                        allowedOption2 = units.filter(
                          (u) => String(u.id) === String(option1Value),
                        );
                      } else if (value2 === 1 && option1Value) {
                        const index = units.findIndex(
                          (u) => String(u.id) === String(option1Value),
                        );
                        if (index > 0) allowedOption2.push(units[index - 1]);
                        if (index < units.length - 1)
                          allowedOption2.push(units[index + 1]);
                      } else if (value2 === 2) {
                        if (!option1Value) {
                          allowedOption2 = units.length
                            ? [units[0], units[units.length - 1]]
                            : [];
                        } else {
                          const index = units.findIndex(
                            (u) => String(u.id) === String(option1Value),
                          );
                          if (index === 0 && units.length) {
                            allowedOption2 = [units[units.length - 1]];
                          } else if (
                            index === units.length - 1 &&
                            units.length
                          ) {
                            allowedOption2 = [units[0]];
                          } else if (units.length) {
                            // If a middle element was selected, still restrict to first and last
                            allowedOption2 = [
                              units[0],
                              units[units.length - 1],
                            ];
                          } else {
                            allowedOption2 = [];
                          }
                        }
                      } else {
                        allowedOption2 = units;
                      }

                      return (
                        <Form.Item
                          name={[restrictionIdx, 'option2']}
                          rules={[
                            {
                              required: true,
                              message: t(
                                'entity.offers.restrictions.restriction.ZONAL.axisRequired',
                              ),
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (
                                  !value ||
                                  String(value) !== String(option1Value) ||
                                  value2 === ZERO_TRANSITIONS_NO
                                ) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(
                                  new Error(
                                    t(
                                      'entity.offers.restrictions.restriction.ZONAL.sameUatNotAllowed',
                                    ),
                                  ),
                                );
                              },
                            }),
                          ]}
                        >
                          <Select
                            placeholder={t(
                              'entity.offers.restrictions.restriction.ZONAL.uatStop',
                            )}
                            style={{ width: '100%' }}
                            disabled={isPrefilled}
                            allowClear={!isPrefilled}
                          >
                            {allowedOption2.map((unit) => (
                              <Select.Option key={unit.id} value={unit.id}>
                                {unit.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Descriptions.Item>
              )}
            </>
          )
        ) : (
          <Descriptions.Item>
            {t('entity.offers.restrictions.restriction.ZONAL.nTransitions')}
          </Descriptions.Item>
        ))}
    </Descriptions>
  );
};

export default ZonalRestriction;
